import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { ApiError } from '../api/client';
import { getMe, sendSessionActivity, type MeResponse } from '../api/management';
import { Loader } from '../components/ui/Loader';

type AdminSessionContextValue = {
  session: MeResponse;
  refresh: () => Promise<void>;
};

const AdminSessionContext = createContext<AdminSessionContextValue | null>(null);

export function useAdminSession() {
  const value = useContext(AdminSessionContext);
  if (!value) throw new Error('useAdminSession must be used inside AdminSessionGuard');
  return value;
}

type AdminSessionGuardProps = {
  fallback?: ReactNode;
};

export default function AdminSessionGuard({ fallback = null }: AdminSessionGuardProps) {
  const [session, setSession] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const mounted = useRef(true);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getMe();
      if (mounted.current) setSession(result);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      if (mounted.current) setError(err instanceof Error ? err.message : 'Session check failed');
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (session?.user.role !== 'ADMIN') return;
    let lastSent = 0;
    const send = () => {
      const now = Date.now();
      if (now - lastSent < 60_000) return;
      lastSent = now;
      void sendSessionActivity().catch(() => undefined);
    };
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((eventName) => window.addEventListener(eventName, send, { passive: true }));
    const onVisibility = () => { if (document.visibilityState === 'visible') send(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, send));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [session?.user.role]);

  const value = useMemo<AdminSessionContextValue | null>(() => session ? ({ session, refresh: load }) : null, [session]);

  if (loading) return <Loader fullScreen label="Checking admin session…" />;
  if (error) {
    return (
      <main className="grid min-h-screen place-items-center bg-slate-100 p-4">
        <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-lg font-bold text-slate-900">Session check failed</h1>
          <p className="mt-2 text-sm text-red-700">{error}</p>
          <button type="button" className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white" onClick={() => void load()}>Retry</button>
        </div>
      </main>
    );
  }
  if (!session) return null;
  if (!['ADMIN', 'SUPER_MASTER', 'MASTER', 'DISTRIBUTOR'].includes(session.user.role)) return <>{fallback}</>;

  return <AdminSessionContext.Provider value={value}><Outlet /></AdminSessionContext.Provider>;
}
